import React from 'react'

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material'
import { planLabel } from 'features/customers/constants/planLabels'
import { useCustomerCreate } from 'features/customers/hooks/useCustomerCreate'
import { usePlans } from 'features/customers/hooks/usePlans'
import { customerCreateValidator } from 'features/customers/validators/create'
import { useWatch } from 'react-hook-form'

import { EPlanName } from '@albacross/types'

import { FormSearchSelect, FormSelect, FormTextField } from 'components/input'
import { withForm } from 'decorators/withForm'

import { useCountries } from '../../../../hooks/useCountries'
import { paymentMethodOpts } from '../../utils/customers'

const toOptions = (map: Record<string, string>) => Object.entries(map).map(([value, label]) => ({ value, label }))

const billingPeriodOptions = (plan: any) => {
  switch (plan) {
    case EPlanName.Starter:
      return toOptions({ monthly: 'monthly' })
    case EPlanName.Pro:
    case EPlanName.Enterprise:
      return toOptions({ annual: 'annual' })
    default:
      return toOptions({ monthly: 'monthly', annual: 'annual' })
  }
}

const trialOptions = toOptions({
  '-1': 'None',
  7: '7 days',
  14: '14 days',
  30: '30 days',
})

const plansWithTrialAvailable = (plan: any) => {
  switch (plan) {
    case EPlanName.Business:
    case EPlanName.Pro:
    case EPlanName.Premium:
    case EPlanName.Enterprise:
    case EPlanName.Starter:
    case EPlanName.SlgStarter:
    case EPlanName.SlgProfessional:
    case EPlanName.SlgOrganisation:
      return true
    default:
      return false
  }
}

type CustomerNewFormProps = {
  change: (...args: any[]) => any
  handleSubmit: (...args: any[]) => any
  open: boolean
  onClose: (...args: any[]) => any
}

const CustomerNewForm = ({ handleSubmit, open, onClose, change }: CustomerNewFormProps) => {
  const { data: plans } = usePlans()
  const { mutateAsync: createCustomer, isPending: isLoading } = useCustomerCreate()
  const [paymentMethod, plan] = useWatch({ name: ['paymentMethod', 'plan'] })
  const { countryOptions } = useCountries()

  const onSubmit = async ({ plan: planId, ...rest }) => {
    try {
      await createCustomer({
        ...rest,
        plan: planId && plans?.find(plan => plan.id === +planId)?.name,
      })
    } finally {
      onClose()
    }
  }

  const planOptions = (plans || []).map((p: any) => ({ value: p.id, label: planLabel(p.name) }))
  const selectedPlanName = (plans || []).find((p: any) => p.id === plan)?.name
  const billingPeriods = billingPeriodOptions(selectedPlanName)
  const displayBillingPeriod = plansWithTrialAvailable(selectedPlanName)

  const displayTrialField = paymentMethod === 'card' && displayBillingPeriod

  return (
    <Dialog onClose={onClose} open={open}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Add customer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ width: 500 }}>
            <Grid
              size={{
                sm: 12,
              }}>
              <FormTextField label="Customer name" name="name" />
            </Grid>
            <Grid
              size={{
                sm: 12,
              }}>
              <FormTextField label="Website" name="website" />
            </Grid>
            <Grid
              size={{
                sm: 12,
              }}>
              <FormSearchSelect label="Country" name="country" options={countryOptions} />
            </Grid>
            <Grid
              size={{
                sm: 6,
              }}>
              <FormSelect
                allowEmpty={false}
                label="Plan"
                name="plan"
                // Default billingPeriod to the new plan's first valid option (not null): the field is hidden for some
                // plans, so nulling it left customerCreateValidator unsatisfiable and the form un-submittable.
                onChange={(planId: any) =>
                  change(
                    'billingPeriod',
                    billingPeriodOptions(plans?.find((p: any) => p.id === planId)?.name)[0]?.value,
                  )
                }
                options={planOptions}
              />
            </Grid>
            <Grid
              size={{
                sm: 6,
              }}>
              {displayBillingPeriod && (
                <FormSelect allowEmpty={false} label="Billing period" name="billingPeriod" options={billingPeriods} />
              )}
            </Grid>
            <Grid
              size={{
                sm: 6,
              }}>
              <FormSelect allowEmpty={false} label="Payment method" name="paymentMethod" options={paymentMethodOpts} />
            </Grid>
            <Grid
              size={{
                sm: 6,
              }}>
              {displayTrialField && (
                <FormSelect allowEmpty={false} label="Trial" name="trialDays" options={trialOptions} />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button color="primary" loading={isLoading} type="submit">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default withForm({
  form: 'customer',
  validate: customerCreateValidator,
  initialValues: {
    billingPeriod: 'annual',
    country: 'SE',
  },
})(CustomerNewForm)
